'use client';
import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { breakpointMobile } from 'src/contexts/era-package/config';
import { useERACustomizer } from 'src/contexts/era-package/EraCustomizerContext';

/**
 * ERA² Responsive Button Layout Component
 *
 * This component provides a standardized layout system for form and dialog buttons,
 * automatically handling responsive behavior, internationalization (LTR/RTL), and
 * semantic button grouping.
 *
 * DESIGN PHILOSOPHY:
 * =================
 * - Advance buttons (Submit, Next, Save) go on the right in LTR layouts
 * - Other buttons (Cancel, Clear, Delete) go on the left in LTR layouts
 * - Layout automatically reverses for RTL languages (Arabic, Hebrew, etc.)
 * - Mobile layouts stack buttons vertically with consistent spacing
 * - Integrates with global theme direction from Redux store
 *
 * RESPONSIVE BEHAVIOR:
 * ===================
 * Desktop: [Other Buttons] ---------- [Advance Buttons]
 * Mobile:  [Advance Buttons]
 *          [Other Buttons]
 *
 * Note: Mobile layout reverses order so primary actions appear first
 *
 * @example
 * // Basic form buttons
 * <Buttons
 *   other={<Button variant="outlined">Cancel</Button>}
 *   advance={<Button variant="contained">Submit</Button>}
 * />
 *
 * @example
 * // Multiple buttons with custom spacing
 * <Buttons
 *   other={
 *     <>
 *       <Button color="error">Delete</Button>
 *       <Button variant="outlined">Clear</Button>
 *     </>
 *   }
 *   advance={
 *     <>
 *       <Button variant="outlined">Save Draft</Button>
 *       <Button variant="contained">Publish</Button>
 *     </>
 *   }
 *   buttonGap="0.5rem"
 *   blockGap="2rem"
 * />
 *
 * @example
 * // RTL layout override
 * <Buttons
 *   direction="rtl"
 *   other={<Button>إلغاء</Button>}
 *   advance={<Button variant="contained">إرسال</Button>}
 * />
 *
 * @example
 * // Custom mobile breakpoint
 * <Buttons
 *   breakpoint={768}
 *   blockGapMobile="0.5rem"
 *   buttonGapMobile="0.25rem"
 *   advance={<Button>Continue</Button>}
 * />
 *
 * @example
 * // Using additional HTML/CSS props
 * <Buttons
 *   id="form-actions"
 *   className="custom-button-container"
 *   style={{ marginTop: '2rem' }}
 *   data-testid="submit-form-buttons"
 *   role="group"
 *   aria-label="Form actions"
 *   advance={<Button variant="contained">Submit</Button>}
 *   other={<Button variant="outlined">Cancel</Button>}
 * />
 */

/**
 * Styled container that handles complex flex layout with responsive behavior.
 *
 * KEY CSS STRATEGIES:
 * ==================
 *
 * 1. FLEX-WRAP-REVERSE TECHNIQUE:
 *    - Uses flex-wrap: wrap-reverse to control button order
 *    - Allows semantic HTML order while controlling visual presentation
 *    - Ensures mobile-first responsive behavior works correctly
 *
 * 2. DIRECTION HANDLING:
 *    - flex-direction: row vs row-reverse for LTR/RTL support
 *    - Maintains consistent button semantics across languages
 *    - Works with both explicit direction props and Redux global settings
 *
 * 3. RESPONSIVE LAYOUT:
 *    - Desktop: Horizontal layout with space-between
 *    - Mobile: Vertical stack with advance buttons first
 *    - Customizable breakpoint for different screen size requirements
 *
 * 4. NESTED FLEX CONTAINERS:
 *    - Outer container: Controls block positioning (left/right)
 *    - Inner containers: Control button grouping within each block
 *    - Each level has independent gap controls for fine-tuned spacing
 *
 * 5. ADDITIONAL PROPS HANDLING:
 *    - Accepts any valid HTML div attributes via ...props
 *    - Useful for adding custom styling, data attributes, ARIA labels, etc.
 *    - Props are applied to the root container element
 */
const BlockContainer = styled('div')(({ theme, ...props }) => ({
  // CONTAINER SETUP: Force LTR reading direction for consistent behavior
  direction: 'ltr', // Always LTR for the container, individual buttons handle their own direction
  width: '100%',
  display: 'flex',

  // FLEX BEHAVIOR: Wrap-reverse allows semantic HTML order while controlling visual order
  flexWrap: 'wrap-reverse', // Critical for mobile responsive behavior

  // DIRECTION CONTROL: Handle LTR/RTL layout direction
  flexDirection: props.direction === 'ltr' ? 'row' : 'row-reverse',

  // SPACING: Distribute blocks to opposite ends with gap between
  justifyContent: 'space-between', // Push blocks to opposite sides
  gap: props.blockGap, // Space between the two button blocks

  // MOBILE RESPONSIVE: Stack vertically below breakpoint
  [`@media (max-width: ${props.breakpoint}px)`]: {
    flexDirection: 'column-reverse', // Advance buttons first in mobile
    gap: props.blockGapMobile, // Different spacing for mobile
  },

  // BUTTON BLOCK STYLING: Each div contains a group of related buttons
  '& > div': {
    direction: 'ltr', // Consistent direction for button containers
    flexGrow: 1, // Allow blocks to expand to fill available space
    display: 'flex',
    flexWrap: 'wrap-reverse', // Handle button wrapping within blocks

    // BLOCK DIRECTION: Match parent direction for consistent behavior
    flexDirection: props.direction === 'ltr' ? 'row' : 'row-reverse',

    // BUTTON ALIGNMENT: Default alignment for button groups
    justifyContent: 'flex-start', // Align buttons to start of their container
    gap: props.buttonGap, // Space between individual buttons

    // ADVANCE BLOCK ALIGNMENT: Right-align advance buttons in their container
    '&:last-child': {
      justifyContent: 'flex-end', // Advance buttons align to end (right in LTR)
    },

    // MOBILE BUTTON LAYOUT: Vertical stacking with custom spacing
    [`@media (max-width: ${props.breakpoint}px)`]: {
      flexDirection: 'column-reverse', // Stack buttons vertically
      gap: props.buttonGapMobile, // Different button spacing for mobile
    },
  },
}));

/**
 * COMPONENT PROP DOCUMENTATION
 * ============================
 *
 * The component accepts several props for controlling layout, spacing, and behavior.
 * Most props have sensible defaults and can be omitted for standard usage.
 *
 * ADDITIONAL PROPS (...props):
 * ============================
 * The component accepts any additional HTML div attributes through the spread operator.
 * These props are passed directly to the root container element and can be used for:
 *
 * COMMON USE CASES:
 * - id: Unique identifier for the button container
 * - className: Additional CSS classes for custom styling
 * - style: Inline styles for quick customizations
 * - data-*: Data attributes for analytics, testing, or custom functionality
 * - aria-*: Accessibility attributes for screen readers
 * - role: ARIA role for semantic meaning
 * - onMouseEnter/onMouseLeave: Mouse event handlers
 * - onClick: Click handlers (though individual buttons should handle their own clicks)
 * - tabIndex: Tab order control (use carefully with button focus management)
 *
 * STYLING INTEGRATION:
 * - Custom CSS classes can override default styles
 * - Inline styles have highest specificity and override styled-components
 * - Data attributes useful for CSS selectors: [data-state="loading"]
 * - Theme integration possible through className and Material-UI theme
 *
 * TESTING & ANALYTICS:
 * - data-testid: Standardized test selectors for automation
 * - data-analytics: Custom tracking attributes
 * - data-component: Component identification for debugging
 *
 * ACCESSIBILITY ENHANCEMENTS:
 * - aria-label: Descriptive label for the button group
 * - aria-describedby: Reference to help text or descriptions
 * - role="group": Semantic grouping for screen readers
 * - aria-labelledby: Reference to heading that describes the button group
 *
 * DIRECTION PROP LOGIC:
 * ====================
 * The direction logic handles both explicit prop overrides and global theme settings:
 *
 * 1. If customizer.activeDir === 'rtl' (global RTL mode):
 *    - Default behavior: Use LTR layout (because most forms work better LTR)
 *    - direction="rtl" prop: Force RTL layout
 *    - direction="ltr" prop: Keep LTR layout (explicit override)
 *
 * 2. If customizer.activeDir !== 'rtl' (global LTR mode):
 *    - Default behavior: Use LTR layout
 *    - direction="rtl" prop: Force RTL layout
 *    - direction="ltr" prop: Keep LTR layout (explicit)
 *
 * This logic allows forms to maintain LTR button order even in RTL interfaces,
 * while still allowing explicit RTL override when needed.
 */
const Buttons = ({
  // LAYOUT DIRECTION: Controls visual flow of button blocks
  direction: directionProp,

  // BUTTON CONTENT: JSX elements for each button group
  advance, // Primary action buttons (Submit, Next, Save, etc.)
  other, // Secondary action buttons (Cancel, Clear, Delete, etc.)

  // RESPONSIVE BEHAVIOR: Control mobile/desktop layout switch
  breakpoint = breakpointMobile, // Pixel width where layout switches to mobile

  // SPACING CONTROLS: Fine-tune visual spacing
  blockGap = '1rem', // Space between advance and other button groups (desktop)
  blockGapMobile, // Space between button groups on mobile (defaults to blockGap)
  buttonGap = '1rem', // Space between buttons within each group (desktop)
  buttonGapMobile, // Space between buttons within groups on mobile (defaults to buttonGap)

  // ADDITIONAL HTML ATTRIBUTES: Any other props are passed to the root container
  ...props // Spread operator to capture all additional props
}) => {
  // REDUX INTEGRATION: Get global theme direction from customizer
  const { activeDir } = useERACustomizer()

  // DIRECTION CALCULATION: Complex logic to handle global RTL vs component-level overrides
  // This ensures proper button layout regardless of global theme direction
  const direction =
    activeDir === 'rtl' // Global RTL mode active?
      ? !directionProp || directionProp === 'rtl' // No override or explicit RTL?
        ? 'ltr' // As the global direction is already set to rtl, nesting a ltr within a global rtl = RTL
        : 'rtl' // Otherwise if "ltr" is specified, nesting a rtl within a global rtl = LTR
      : directionProp === 'rtl' // Global LTR mode, but RTL requested?
        ? 'rtl' // Honor explicit RTL request
        : 'ltr'; // Default LTR behavior

  return (
    <BlockContainer
      // PASS COMPUTED VALUES: Send calculated values to styled component
      direction={direction}
      breakpoint={breakpoint}
      blockGap={blockGap}
      blockGapMobile={blockGapMobile || blockGap} // Default mobile gap to desktop gap
      buttonGap={buttonGap}
      buttonGapMobile={buttonGapMobile || buttonGap} // Default mobile gap to desktop gap
      // SPREAD ADDITIONAL PROPS: Pass through any extra HTML attributes
      {...props}
    >
      {/* BUTTON BLOCKS: Order is significant for responsive behavior */}
      {/* 
        HTML Order vs Visual Order:
        - HTML: other first, advance second (for accessibility)
        - Desktop Visual: other left, advance right (via CSS)
        - Mobile Visual: advance first, other second (via CSS)
      */}
      <div>{other}</div> {/* Secondary actions block */}
      <div>{advance}</div> {/* Primary actions block */}
    </BlockContainer>
  );
};

// COMPREHENSIVE PROP VALIDATION AND DOCUMENTATION
Buttons.propTypes = {
  /**
   * Text direction for button layout. Affects which side advance/other buttons appear on.
   * - 'ltr': Advance buttons on right, other buttons on left
   * - 'rtl': Advance buttons on left, other buttons on right
   * If not specified, automatically detects from Redux customizer.activeDir
   */
  direction: PropTypes.oneOf(['ltr', 'rtl']),

  /**
   * Primary action buttons (Submit, Next, Save, Continue, etc.)
   * These appear on the right in LTR layouts and first in mobile layouts.
   * Can be a single button or multiple buttons wrapped in a fragment.
   */
  advance: PropTypes.node,

  /**
   * Secondary action buttons (Cancel, Clear, Delete, Back, etc.)
   * These appear on the left in LTR layouts and second in mobile layouts.
   * Can be a single button or multiple buttons wrapped in a fragment.
   */
  other: PropTypes.node,

  /**
   * Breakpoint width in pixels for switching between desktop and mobile layouts.
   * Below this width, buttons stack vertically instead of horizontally.
   * Defaults to breakpointMobile from global config.
   */
  breakpoint: PropTypes.number,

  /**
   * CSS gap value between the advance and other button blocks on desktop.
   * Common values: '0.5rem', '1rem', '1.5rem', '2rem'
   */
  blockGap: PropTypes.string,

  /**
   * CSS gap value between the advance and other button blocks on mobile.
   * If not specified, defaults to the value of blockGap.
   */
  blockGapMobile: PropTypes.string,

  /**
   * CSS gap value between individual buttons within each block on desktop.
   * Controls spacing between multiple buttons in the same group.
   */
  buttonGap: PropTypes.string,

  /**
   * CSS gap value between individual buttons within each block on mobile.
   * If not specified, defaults to the value of buttonGap.
   */
  buttonGapMobile: PropTypes.string,

  /**
   * ADDITIONAL HTML ATTRIBUTES (via ...props):
   * ===========================================
   * Any additional props are passed directly to the root container div element.
   * This allows for extensive customization and integration with existing systems.
   *
   * COMMON ADDITIONAL PROPS:
   * ------------------------
   * @param {string} id - Unique identifier for the container
   * @param {string} className - Additional CSS classes for styling
   * @param {Object} style - Inline styles object for quick customizations
   * @param {string} data-testid - Test automation selector
   * @param {string} data-* - Custom data attributes for analytics/functionality
   * @param {string} aria-label - Accessible label for the button group
   * @param {string} aria-describedby - ID of element that describes the group
   * @param {string} role - ARIA role (e.g., "group", "toolbar")
   * @param {Function} onMouseEnter - Mouse enter event handler
   * @param {Function} onMouseLeave - Mouse leave event handler
   * @param {Function} onFocus - Focus event handler
   * @param {Function} onBlur - Blur event handler
   * @param {number} tabIndex - Tab order control (use carefully)
   *
   * STYLING INTEGRATION EXAMPLES:
   * -----------------------------
   * className="custom-buttons elevated-surface"
   * style={{ marginTop: '2rem', padding: '1rem' }}
   * data-theme="dark"
   *
   * ACCESSIBILITY EXAMPLES:
   * -----------------------
   * aria-label="Form submission actions"
   * role="group"
   * aria-describedby="form-help-text"
   *
   * TESTING EXAMPLES:
   * -----------------
   * data-testid="form-actions"
   * data-component="buttons"
   * data-form-step="2"
   *
   * ANALYTICS EXAMPLES:
   * -------------------
   * data-analytics="button-group"
   * data-section="checkout-actions"
   * data-experiment="new-button-layout"
   */
  // Note: We don't explicitly validate ...props since they can be any valid HTML attributes
  // PropTypes.any or a custom validator could be used, but it's generally not necessary
  // for spread props that pass through to native DOM elements
};

// COMPONENT METADATA: For debugging and React DevTools
Buttons.displayName = 'ERA² Buttons';

export default Buttons;

// USAGE PATTERNS AND BEST PRACTICES:
// ==================================
//
// 1. SEMANTIC BUTTON GROUPING:
//    - advance: Actions that move the user forward (Submit, Next, Save, Publish)
//    - other: Actions that are secondary or destructive (Cancel, Clear, Delete, Back)
//
// 2. BUTTON ORDER WITHIN GROUPS:
//    - Place most important action last in the advance group
//    - Place least destructive action first in the other group
//    - This ensures proper focus order and visual hierarchy
//
// 3. RESPONSIVE CONSIDERATIONS:
//    - Test button labels at mobile widths to ensure they don't wrap awkwardly
//    - Consider shorter button labels for mobile if necessary
//    - Use buttonGapMobile to reduce spacing on small screens if needed
//
// 4. INTERNATIONALIZATION:
//    - Component automatically handles RTL layouts when global RTL is enabled
//    - Use direction prop only when you need to override global direction
//    - Test with longer translated text to ensure proper wrapping
//
// 5. ACCESSIBILITY:
//    - Maintain logical tab order by placing advance buttons last in HTML
//    - Use semantic button elements, not divs with click handlers
//    - Ensure sufficient color contrast for all button states
//    - Provide clear, descriptive button labels
//    - Use additional props for ARIA attributes when needed
//
// 6. ADDITIONAL PROPS USAGE:
//
//    // Basic HTML attributes
//    <Buttons
//      id="checkout-actions"
//      className="elevated-surface"
//      advance={<Button>Complete Order</Button>}
//    />
//
//    // Accessibility enhancements
//    <Buttons
//      role="group"
//      aria-label="Form submission actions"
//      aria-describedby="form-help"
//      advance={<Button>Submit</Button>}
//      other={<Button>Cancel</Button>}
//    />
//
//    // Testing and analytics
//    <Buttons
//      data-testid="form-buttons"
//      data-analytics="checkout-step-3"
//      data-form-id="user-registration"
//      advance={<Button>Register</Button>}
//    />
//
//    // Custom styling
//    <Buttons
//      style={{
//        marginTop: '2rem',
//        padding: '1rem',
//        backgroundColor: 'var(--surface-elevated)'
//      }}
//      advance={<Button>Continue</Button>}
//    />
//
//    // Event handling
//    <Buttons
//      onMouseEnter={() => logInteraction('buttons-hover')}
//      onFocus={handleFocus}
//      advance={<Button>Submit</Button>}
//    />
//
// 7. COMMON PATTERNS:
//
//    // Form submission
//    <Buttons
//      other={<Button variant="outlined">Cancel</Button>}
//      advance={<Button variant="contained" type="submit">Submit</Button>}
//    />
//
//    // Multi-step wizard with analytics
//    <Buttons
//      data-step="2"
//      data-total-steps="5"
//      other={<Button onClick={goBack}>Back</Button>}
//      advance={
//        <>
//          <Button variant="outlined" onClick={saveDraft}>Save Draft</Button>
//          <Button variant="contained" onClick={goNext}>Continue</Button>
//        </>
//      }
//    />
//
//    // Destructive action confirmation with accessibility
//    <Buttons
//      role="group"
//      aria-label="Account deletion actions"
//      data-testid="delete-account-buttons"
//      other={
//        <>
//          <Button variant="outlined">Cancel</Button>
//          <Button color="error" onClick={handleDelete}>Delete Account</Button>
//        </>
//      }
//      advance={<Button variant="contained" onClick={confirmAction}>I Understand, Delete</Button>}
//    />
//
//    // Custom themed button container
//    <Buttons
//      className="dark-theme-buttons"
//      style={{ borderRadius: '12px', padding: '1rem' }}
//      data-theme="dark"
//      advance={<Button>Complete</Button>}
//    />
//
// 8. PERFORMANCE CONSIDERATIONS:
//    - Component re-renders when Redux customizer state changes
//    - Consider memoizing if used in performance-critical contexts
//    - Gap props are passed as strings to avoid unnecessary re-calculations
//    - Additional props don't impact performance as they're passed through directly
//
// 9. STYLING INTEGRATION WITH ADDITIONAL PROPS:
//    - Use className for CSS modules or styled-components integration
//    - Use style prop for dynamic inline styles based on component state
//    - Use data-* attributes for CSS selectors: [data-state="loading"] .buttons
//    - Combine with theme systems via className and CSS custom properties
